/* eslint-disable no-restricted-globals */
/* eslint-disable max-len */
/* eslint-disable no-alert */
/* eslint-disable no-nested-ternary */
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import frontSide from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/front-side.svg';
import leftSide from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/left-side.svg';
import lowerArch from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/lower-arch.svg';
import rightSide from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/right-side.svg';
import upperArch from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/upper-arch.svg';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { Pricings } from '@brands/services/identity/getPricings';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { setForm, UploadedFile } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { needsPayment } from '@brands/Utils/needsPayment';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button } from '@mui/material';
import { heicTo } from 'heic-to';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import Container from '../../../../Components/Container/Container';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import useWindowSize from '../../../../hooks/useWindowSize';
import { truncateFilename } from '../../../../Utils/truncateString';
import AddDependentModal from '../../../MyProfile/Patient/Sections/AddDependentModal/AddDependentModal';
import othersType from './assets/others-file-icon.svg';
import Camera from './Components/Camera';
import styles from './styles.module.scss';
import { uploadIntakeImage, uploadIntakeImages } from '@brands/Utils/uploadIntakeImage';
import { clearPreviewCache } from '@brands/Utils/mediaPreviewCache';
import { UploadedFileImage } from '@brands/Components/UploadedFileImage/UploadedFileImage';
import { CASE_ATTACHMENT_ACCEPT, isAcceptedCaseAttachmentFile } from '@brands/Utils/caseAttachmentFiles';

type ImageUploadSubmit = {
  images: UploadedFile[];
};

const dataURLToFile = async (dataURL: string, filename: string): Promise<File> => {
  const response = await fetch(dataURL);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const ImageUpload = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const { values } = useAppSelector(selectPatientForm);
  const { currentOrganizationPrices } = useAppSelector(selectOrganizationPrices);
  const screenSize = useWindowSize();
  const [openCamera, setOpenCamera] = useState(false);
  const [showAddDependentModal, setShowAddDependentModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<UploadedFile[]>(
    (values.find((item) => item.form_question_id === 17)?.value.images as UploadedFile[]) || [],
  );
  // The initializer above only reads Redux once, at first mount — a linked
  // case's prefilled images (completeLinkedCase.ts) can land in Redux after
  // that first render (e.g. this component was already mounted from an
  // earlier step). Re-sync once from Redux the first time it actually has
  // images and local state doesn't, without fighting the patient's own later
  // edits (adding/removing files locally stops this from firing again).
  const appliedPrefillRef = useRef(images.length > 0);
  useEffect(() => {
    if (appliedPrefillRef.current) return;
    const reduxImages = values.find((item) => item.form_question_id === 17)?.value.images as
      | UploadedFile[]
      | undefined;
    if (reduxImages && reduxImages.length > 0) {
      setImages(reduxImages);
      appliedPrefillRef.current = true;
    }
  }, [values]);
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleFooter = (): void => {
    setIsExpanded((prev) => !prev);
  };
  const searchParams = new URLSearchParams(location.search);
  const routeCaseType = searchParams.get('case_type');
  const caseType: string = !routeCaseType
    ? (values.find((item) => item.form_question_id === 20)?.value.caseType as string)
    : routeCaseType;

  const currentOrganizationPrice = currentOrganizationPrices?.find((currentPrice: Pricings) =>
    currentPrice.case_types.includes(caseType as string),
  );

  const onSubmit = (data: ImageUploadSubmit): void => {
    const payload = [...values!];
    const filteredPayload = payload.filter((item) => item.form_question_id !== 17);

    filteredPayload.push({
      form_question_id: 17,
      value: { ...data },
    });

    dispatch(setForm(filteredPayload));
    if (caseType === CaseType.clinical_enablement) {
      // Clinical Enablement collects photos first so the medical-history
      // steps that follow can tell whether a file was already attached.
      roleNavigate('/clinical-data');
    } else {
      roleNavigate(needsPayment(currentOrganizationPrice) ? '/payment-information' : '/confirm-payment');
    }
  };

  const onBack = (): void => {
    dispatch(setForm([...values!]));
    navigate(-1);
  };

  const onInvalid = (errors: any): void => {
    const error = Object.values(errors)[0] as any;
    if (error) {
      toast.error(error.message as string);
    }
  };

  const validationSchema = Yup.object().shape({
    images:
      (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report'
        ? Yup.array()
            .min(1, 'At least 1 images are required to upload.')
            .max(10, 'Only 10 images are allowed to upload.')
            .required('At least 1 images are required to upload.')
        : (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'second_opinion'
        ? Yup.array()
            .min(1, 'At least 1 attachment is required to upload.')
            .max(10, 'Only 10 attachments are allowed to upload.')
            .required('At least 1 attachment is required to upload.')
        : Yup.array().max(10, 'Only 10 images are allowed to upload.').nullable(),
  });

  const { setValue, register, handleSubmit } = useForm<ImageUploadSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onCloseCamera = (): void => {
    setOpenCamera(false);
  };

  const onPhotoTaken = async (uri: string): Promise<void> => {
    try {
      setUploading(true);
      const file = await dataURLToFile(uri, 'Capture.png');
      const uploaded = await uploadIntakeImage(file, userInfo?.role?.name);
      setImages((prev) => [...prev, uploaded]);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setUploading(false);
    }
  };

  const addImages = async (files: File[]): Promise<void> => {
    const acceptedFiles = files.filter(isAcceptedCaseAttachmentFile);
    if (images.length + acceptedFiles.length > 10) {
      toast.error(
        (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report'
          ? 'Only 1-10 files are allowed to upload.'
          : 'Max 10 files are allowed to upload.',
      );
      return;
    }
    setUploading(true);
    try {
      const successful = await uploadIntakeImages(acceptedFiles, userInfo?.role?.name);
      if (successful.length > 0) {
        setImages((prev) => uniqBy([...prev, ...successful], 'media_id'));
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (event: any): void => {
    event.preventDefault();
  };

  // See default-brand ImageUpload.tsx — both `image/heic` and
  // `image/heif` need the heicTo conversion or the upload backend
  // rejects them as raw bytes. Both `handleChange` (file picker)
  // and `handleDrop` (drag-and-drop) need to run through this
  // helper before forwarding to `addImages`.
  const convertHeicFiles = async (rawFiles: File[]): Promise<File[]> =>
    Promise.all(
      rawFiles.map(async (file) => {
        if (file.type === 'image/heic' || file.type === 'image/heif') {
          try {
            const jpegBlob = await heicTo({
              blob: file,
              type: 'image/jpeg',
              quality: 0.85,
            });
            return new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpeg'), { type: 'image/jpeg' });
          } catch (error: unknown) {
            displayErrorDetails(`Error converting HEIC to JPEG: ${error}`);
            return file;
          }
        }
        return file;
      }),
    );

  const handleDrop = async (event: React.DragEvent<HTMLImageElement>): Promise<void> => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const converted = await convertHeicFiles(files);
    void addImages(converted);
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files as unknown as File[]);
    const converted = await convertHeicFiles(files);
    void addImages(converted);
  };

  const deleteMedia = (index: number): void => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.media_id) clearPreviewCache(removed.media_id);
      return prev.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    setValue('images', images);

    const filteredPayload = values.filter((item) => item.form_question_id !== 17);

    dispatch(
      setForm([
        ...filteredPayload,
        {
          form_question_id: 17,
          value: { images },
        },
      ]),
    );
  }, [images]);

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{ zIndex: '2', paddingTop: screenSize.width > 768 ? '40px' : '16px' }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={styles.uploadImageContainer}>
        <div className={styles.uploadImageContainerDiv}>
          <div className={styles.patientSelectTopDivContainer}>
            <PatientIntakeNavigation progress={80} />
          </div>
          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div className={styles.wrapper}>
              <div className={styles.formContent}>
                <div className={styles.uploadImageContainerHeaderDiv}>
                  <span className={`fs-unmask ${styles.uploadImageContainerHeader}`}>Attachment upload</span>
                  <span className={`fs-unmask ${styles.uploadImageContainerHeaderText}`}>
                    Please upload up to 10 of your recent reports, medical scans, doctors notes, and other records that
                    will help the dentist complete the consultation (at least one attachment is required).
                  </span>
                </div>
                {images.length > 0 ? (
                  <div className={`fs-unmask ${styles.grid}`}>
                    {images.map((file, i) => {
                      const mediaId = file.media_id ?? '';
                      const isUploadedImage = (file.mimetype ?? '').startsWith('image/');
                      const isLegacyDataUrlImage = file.dataURL?.startsWith('data:image/');

                      return (
                        <div className={`fs-exclude ${styles.imageWrap}`} key={mediaId || file.name || i}>
                          <span
                            className={styles.removeImage}
                            onClick={() => deleteMedia(i)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && deleteMedia(i)}
                          >
                            <IoClose />
                          </span>
                          {isUploadedImage && mediaId ? (
                            <UploadedFileImage file={file} userRole={userInfo?.role?.name} />
                          ) : isLegacyDataUrlImage ? (
                            <img src={file.dataURL} alt={file.name} className={styles.image} />
                          ) : (
                            <img className={styles.othersTypeIcon} src={othersType} alt="" />
                          )}
                          <span className={styles.imageFooter}>
                            <p>{truncateFilename(file.name, 20)}</p>
                          </span>
                        </div>
                      );
                    })}
                    {images.length < 10 && (
                      <label htmlFor="addMoreImagesInput" className={`${styles.imageWrap} ${styles.addMoreTile}`}>
                        <div className={styles.addMoreContent}>
                          <AddIcon className={styles.plusIcon} />
                        </div>
                        <input
                          type="file"
                          id="addMoreImagesInput"
                          className="fs-exclude"
                          hidden
                          multiple
                          onChange={handleChange}
                          accept={CASE_ATTACHMENT_ACCEPT}
                        />
                      </label>
                    )}
                  </div>
                ) : (
                  <div
                    className={`${styles.draggingArea} fs-unmask`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <p>Add an image or a file</p>
                    <div className={styles.btnWrap}>
                      <label htmlFor="formId" className={styles.uploadImageLabel}>
                        <input
                          {...register('images')}
                          name=""
                          type="file"
                          id="formId"
                          className="fs-exclude"
                          onChange={handleChange}
                          multiple
                          hidden
                          data-testid="image-upload-select-files"
                          accept={CASE_ATTACHMENT_ACCEPT}
                        />
                        <div className={styles.selectImages}>
                          <UploadFileIcon />
                          <span>Select Files</span>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className={`fs-unmask ${styles.formContentFooter}`}>
                <div className={`fs-unmask ${styles.formContentFooterHeader}`} onClick={toggleFooter}>
                  <span>Need some examples?</span>
                  <span>{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</span>
                </div>
                {isExpanded && (
                  <>
                    <div className={styles.formContentFooterImageDiv}>
                      <span className={styles.formContentFooterImageSpan}>
                        <img src={rightSide} />
                      </span>
                      <div className={styles.formContentFooterImageText}>
                        <span>Right Side</span>
                        <span>
                          Turn your head slightly to the left, and use your index and middle fingers of your right hand
                          to pull back your right cheek.
                        </span>
                      </div>
                    </div>
                    <div className={styles.formContentFooterImageDiv}>
                      <span className={styles.formContentFooterImageSpan}>
                        <img src={leftSide} />
                      </span>
                      <div className={styles.formContentFooterImageText}>
                        <span>Left Side</span>
                        <span>
                          Turn your head slightly to the right, and use your index and middle fingers of your left hand
                          to pull back your left cheek.
                        </span>
                      </div>
                    </div>
                    <div className={styles.formContentFooterImageDiv}>
                      <span className={styles.formContentFooterImageSpan}>
                        <img src={frontSide} />
                      </span>
                      <div className={styles.formContentFooterImageText}>
                        <span>Front</span>
                        <span>
                          With a broad smile and your teeth slightly parted reveal your front teeth and a portion of
                          your gums. You may find it helpful to use your thumb and forefinger to gently widen your lips.
                        </span>
                      </div>
                    </div>
                    <div className={styles.formContentFooterImageDiv}>
                      <span className={styles.formContentFooterImageSpan}>
                        <img src={lowerArch} />
                      </span>
                      <div className={styles.formContentFooterImageText}>
                        <span>Lower Arch</span>
                        <span>
                          With a broad smile and your teeth slightly parted reveal your front teeth and a portion of
                          your gums. You may find it helpful to use your thumb and forefinger to gently widen your lips.
                        </span>
                      </div>
                    </div>
                    <div className={styles.formContentFooterImageDiv}>
                      <span className={styles.formContentFooterImageSpan}>
                        <img src={upperArch} />
                      </span>
                      <div className={styles.formContentFooterImageText}>
                        <span>Upper Arch</span>
                        <span>
                          Flip your phone upside down to capture a better angle, and widen your mouth as much as
                          possible to ensure a clear photograph of your upper teeth.
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={styles.uploadImageContainerDivFooter}>
                <Button onClick={onBack} className={styles.backBtn}>
                  <ArrowBackIosIcon />
                  Back
                </Button>
                {(values.find((item) => item.form_question_id === 20)?.value.caseType as string) !==
                  'second_opinion' && (
                  <div className={styles.btnContainer}>
                    <Button
                      type="submit"
                      className="fs-unmask"
                      disabled={
                        uploading ||
                        ((values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                          'one_report' &&
                          (images.length < 1 || images.length > 10)) ||
                        ((values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                          'second_opinion' &&
                          (images.length === 0 || images.length > 10))
                      }
                    >
                      {(values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                        'one_report' ||
                      (values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                        'second_opinion'
                        ? 'Next'
                        : images.length > 0
                        ? 'Next'
                        : 'Skip'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </form>
          {openCamera && <Camera onClose={onCloseCamera} onPhotoTaken={onPhotoTaken} />}
        </div>
      </div>
      {showAddDependentModal && (
        <AddDependentModal isOpen={showAddDependentModal} setIsOpen={setShowAddDependentModal} isFromSelectNeeds />
      )}
    </Container>
  );
};
export default ImageUpload;
